// Stock and sales data for the Admin: URL search params, API calls and TanStack Query
// options. Every response is parsed with the shared @enp/domain/api schema (lib/api.ts).

import {
  SALE_TICKET_STATUSES,
  SALE_TICKET_TYPES,
  saleTicketDetailSchema,
  salesChannelsResponseSchema,
  salesSummarySchema,
  salesTicketsResponseSchema,
  stockLevelsResponseSchema,
  stockLocationsResponseSchema,
  stockMovementsResponseSchema,
  stockProductLotsResponseSchema,
} from '@enp/domain/api';
import { stateKeySchema } from '@enp/domain/catalogue';
import { REASON_CODES } from '@enp/domain/ledger';
import { isPlainDate } from '@enp/domain/time';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '../lib/api.js';

/** Rows per page of the stock and sales lists. */
export const PAGE_SIZE = 50;

const plainDate = z.string().refine(isPlainDate);

/**
 * A text search in the URL. The router reads `?q=2003` as the number 2003, so a number is
 * taken as its text (a receipt number is still a search).
 */
const search = z
  .preprocess((v) => (typeof v === 'number' ? String(v) : v), z.string().trim().min(1).max(100))
  .optional()
  .catch(undefined);

/** Drops undefined keys, so equal searches compare (and cache) equal. */
export function clean<T extends object>(search: T): T {
  return Object.fromEntries(Object.entries(search).filter(([, v]) => v !== undefined)) as T;
}

// ---------------------------------------------------------------- levels

/** The stock list's state in the URL. Anything invalid is dropped rather than failing. */
export const stockSearchSchema = z.object({
  q: search,
  location: z.uuid().optional().catch(undefined),
  state: stateKeySchema.optional().catch(undefined),
  /** Only lots near expiry (best-before within a couple of days). */
  near: z.literal(true).optional().catch(undefined),
});
export type StockSearch = z.infer<typeof stockSearchSchema>;

export function isStockFiltered(s: StockSearch): boolean {
  return Boolean(s.q || s.location || s.state || s.near);
}

/** The API query string for a page of levels. `bestBeforeBy` is the near-expiry cut-off. */
export function levelsQueryString(
  search: StockSearch,
  bestBeforeBy: string,
  cursor: string | null,
): string {
  const params = new URLSearchParams();
  if (search.q) params.set('q', search.q);
  if (search.location) params.set('location', search.location);
  if (search.state) params.set('state', search.state);
  if (search.near) params.set('best_before_by', bestBeforeBy);
  params.set('limit', String(PAGE_SIZE));
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}

export const stockKeys = {
  levels: (search: StockSearch, by: string) => ['stock', 'levels', clean(search), by] as const,
  locations: () => ['stock', 'locations'] as const,
  lots: (productId: string) => ['stock', 'lots', productId] as const,
  movements: (search: MovementsSearch) => ['stock', 'movements', clean(search)] as const,
  tickets: (search: TicketsSearch) => ['sales', 'tickets', clean(search)] as const,
  summary: (search: TicketsSearch) => ['sales', 'summary', clean(search)] as const,
  channels: () => ['sales', 'channels'] as const,
  ticket: (id: string) => ['sales', 'ticket', id] as const,
};

export const levelsQuery = (search: StockSearch, bestBeforeBy: string) =>
  infiniteQueryOptions({
    queryKey: stockKeys.levels(search, bestBeforeBy),
    queryFn: ({ pageParam, signal }) =>
      api(`/stock/levels?${levelsQueryString(search, bestBeforeBy, pageParam)}`, {
        signal,
        schema: stockLevelsResponseSchema,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    retry: false,
  });

export const locationsQuery = () =>
  queryOptions({
    queryKey: stockKeys.locations(),
    queryFn: ({ signal }) =>
      api('/stock/locations', { signal, schema: stockLocationsResponseSchema }),
    staleTime: 5 * 60_000,
    retry: false,
  });

export const lotsQuery = (productId: string) =>
  queryOptions({
    queryKey: stockKeys.lots(productId),
    queryFn: ({ signal }) =>
      api(`/stock/products/${encodeURIComponent(productId)}/lots`, {
        signal,
        schema: stockProductLotsResponseSchema,
      }),
    retry: false,
  });

// ---------------------------------------------------------------- movements

const dateRange = {
  from: plainDate.optional().catch(undefined),
  to: plainDate.optional().catch(undefined),
};

/**
 * The ledger's filters in the URL: a product (or part of a product's name), a lot, why it
 * moved, where, and a range of trading days.
 */
export const movementsSearchSchema = z.object({
  product: z.uuid().optional().catch(undefined),
  lot: z.uuid().optional().catch(undefined),
  q: search,
  reason: z.enum(REASON_CODES).optional().catch(undefined),
  location: z.uuid().optional().catch(undefined),
  ...dateRange,
});
export type MovementsSearch = z.infer<typeof movementsSearchSchema>;

/** A reversed range is swapped rather than refused (the API would answer 400). */
export function orderedRange<T extends { from?: string; to?: string }>(s: T): T {
  return s.from && s.to && s.from > s.to ? { ...s, from: s.to, to: s.from } : s;
}

export function movementsQueryString(search: MovementsSearch, cursor: string | null): string {
  const s = orderedRange(search);
  const params = new URLSearchParams();
  if (s.product) params.set('product', s.product);
  if (s.lot) params.set('lot', s.lot);
  if (s.q) params.set('q', s.q);
  if (s.reason) params.set('reason', s.reason);
  if (s.location) params.set('location', s.location);
  if (s.from) params.set('from', s.from);
  if (s.to) params.set('to', s.to);
  params.set('limit', String(PAGE_SIZE));
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}

export const movementsQuery = (search: MovementsSearch) =>
  infiniteQueryOptions({
    queryKey: stockKeys.movements(search),
    queryFn: ({ pageParam, signal }) =>
      api(`/stock/movements?${movementsQueryString(search, pageParam)}`, {
        signal,
        schema: stockMovementsResponseSchema,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    retry: false,
  });

// ---------------------------------------------------------------- sales

/** The sales list's filters in the URL: days, receipt search, channel, status and type. */
export const ticketsSearchSchema = z.object({
  ...dateRange,
  q: search,
  channel: z.uuid().optional().catch(undefined),
  status: z.enum(SALE_TICKET_STATUSES).optional().catch(undefined),
  type: z.enum(SALE_TICKET_TYPES).optional().catch(undefined),
});
export type TicketsSearch = z.infer<typeof ticketsSearchSchema>;

/** The API query for the tickets (with a page) or their summary (without). */
export function ticketsQueryString(search: TicketsSearch, cursor: string | null | false): string {
  const s = orderedRange(search);
  const params = new URLSearchParams();
  if (s.from) params.set('from', s.from);
  if (s.to) params.set('to', s.to);
  if (s.q) params.set('q', s.q);
  if (s.channel) params.set('channel', s.channel);
  if (s.status) params.set('status', s.status);
  if (s.type) params.set('type', s.type);
  if (cursor !== false) {
    params.set('limit', String(PAGE_SIZE));
    if (cursor) params.set('cursor', cursor);
  }
  return params.toString();
}

/** Totals of every ticket the filters match (UI2A-5). */
export const salesSummaryQuery = (search: TicketsSearch) =>
  queryOptions({
    queryKey: stockKeys.summary(search),
    queryFn: ({ signal }) =>
      api(`/sales/summary?${ticketsQueryString(search, false)}`, {
        signal,
        schema: salesSummarySchema,
      }),
    retry: false,
  });

/** The channels to filter by. */
export const salesChannelsQuery = () =>
  queryOptions({
    queryKey: stockKeys.channels(),
    queryFn: ({ signal }) =>
      api('/sales/channels', { signal, schema: salesChannelsResponseSchema }),
    staleTime: 5 * 60_000,
    retry: false,
  });

export const ticketsQuery = (search: TicketsSearch) =>
  infiniteQueryOptions({
    queryKey: stockKeys.tickets(search),
    queryFn: ({ pageParam, signal }) =>
      api(`/sales/tickets?${ticketsQueryString(search, pageParam)}`, {
        signal,
        schema: salesTicketsResponseSchema,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    retry: false,
  });

export const ticketQuery = (id: string) =>
  queryOptions({
    queryKey: stockKeys.ticket(id),
    queryFn: ({ signal }) =>
      api(`/sales/tickets/${encodeURIComponent(id)}`, { signal, schema: saleTicketDetailSchema }),
    retry: false,
  });
